import { useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { PROMPT_CATEGORIES, PROMPT_LIBRARY } from "@/lib/prompts";
import {
  Search, ArrowRight, Pin, PinOff, Sparkles, SpellCheck, Scissors, Baby, BookOpen, Mic, Feather,
  Hash, Video, MessageSquare, UserRound, Mail, CalendarDays, FileText, MessagesSquare, Send,
  TrendingUp, ClipboardList, Briefcase, Target, Tag, PiggyBank, Megaphone, ReceiptText,
  CalendarCheck, ListChecks, GraduationCap, AlignLeft, Layers, PencilLine, UtensilsCrossed, Boxes,
  HeartPulse, Gift, Map, HandHeart, PartyPopper, Puzzle, Moon, Cat, Swords, Heart, Languages,
  HelpCircle, Store, ChefHat, Phone, FileCheck, Share2, Pencil, Trash2, Wand2, Rocket, Lightbulb,
  Star, PenLine, CopyPlus, BarChart3, GripVertical,
} from "lucide-react";

const ICONS = {
  Sparkles, SpellCheck, Scissors, Baby, BookOpen, Mic, Feather, Hash, Video, MessageSquare,
  UserRound, Mail, CalendarDays, FileText, MessagesSquare, Send, TrendingUp, ClipboardList,
  Briefcase, Target, Tag, PiggyBank, Megaphone, Search, ReceiptText, CalendarCheck, ListChecks,
  GraduationCap, AlignLeft, Layers, PencilLine, UtensilsCrossed, Boxes, HeartPulse, Gift, Map,
  HandHeart, PartyPopper, Puzzle, Moon, Cat, Swords, Heart, Languages, HelpCircle, Store, ChefHat,
  Phone, FileCheck, Wand2, Rocket, Lightbulb, Star, PenLine,
};

export const PromptIcon = ({ name, className }) => {
  const Glyph = ICONS[name] || Sparkles;
  return <Glyph className={className} />;
};

export const PromptList = ({
  pins = [], onTogglePin, onPick, custom = [], onEdit, onDelete, onDuplicate, onReorder,
  usage = {}, twoColumns = false,
}) => {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [byUse, setByUse] = useState(false);
  const [dragId, setDragId] = useState(null);

  const uses = (p) => usage[`tool:${p.id}`] || 0;

  const drop = (target) => {
    if (!dragId || !onReorder || dragId === target.id) return;
    const dragged = custom.find((p) => p.id === dragId);
    setDragId(null);
    if (!dragged) return;
    const ids = custom.map((p) => p.id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(target.id), 0, dragId);
    const folder = target.folder || "";
    onReorder(ids, (dragged.folder || "") === folder ? undefined : { move_id: dragId, folder });
  };

  const dropOnFolder = (folder) => {
    if (!dragId || !onReorder) return;
    const dragged = custom.find((p) => p.id === dragId);
    setDragId(null);
    if (!dragged || (dragged.folder || "") === folder) return;
    const ids = custom.map((p) => p.id).filter((id) => id !== dragId);
    const last = custom.filter((p) => (p.folder || "") === folder).slice(-1)[0];
    ids.splice(last ? ids.indexOf(last.id) + 1 : ids.length, 0, dragId);
    onReorder(ids, { move_id: dragId, folder });
  };

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const matches = (p) =>
      !term ||
      p.label.toLowerCase().includes(term) ||
      (p.blurb || "").toLowerCase().includes(term) ||
      p.starter.toLowerCase().includes(term);
    const visibleMine = (!cat || cat === "mine" ? custom : []).filter(matches);
    const folders = [...new Set(visibleMine.map((p) => p.folder || ""))].sort();
    const mineGroups = folders.map((folder) => ({
      id: `mine-${folder || "none"}`,
      label: folder ? `My prompts · ${folder}` : "My prompts",
      tint: "text-brand",
      mine: true,
      items: visibleMine
        .filter((p) => (p.folder || "") === folder)
        .sort((a, b) => (byUse ? uses(b) - uses(a) : 0)),
    }));
    const rest = PROMPT_CATEGORIES.map((group) => ({
      ...group,
      items: PROMPT_LIBRARY.filter((p) => p.category === group.id && (!cat || cat === group.id) && matches(p))
        .slice()
        .sort((a, b) => (byUse ? uses(b) - uses(a) : 0)),
    }));
    return [...mineGroups, ...rest].filter((group) => group.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, cat, custom, byUse, usage]);

  const shown = groups.reduce((n, g) => n + g.items.length, 0);
  const total = PROMPT_LIBRARY.length + custom.length;

  const share = async (p) => {
    try {
      const { data } = await api.post("/prompts/share", {
        prompt_key: `${p.mine ? "mine" : "tool"}:${p.id}`,
        label: p.label,
        blurb: p.blurb || "",
        image: p.tile || "",
        ask: p.ask || "",
        starter: p.starter,
        icon: p.icon || "",
      });
      const link = `${window.location.origin}/p/${data.share_id}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Link copied — your friend can use this prompt straight away");
      } catch {
        toast.message(link);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not make a link for that prompt");
    }
  };

  return (
    <>
      <div className="sticky top-0 z-10 border-b border-white/10 bg-panel/95 px-6 pb-4 pt-6 backdrop-blur">
        <p className="font-display text-2xl font-extrabold tracking-tighter">{"Popular\u00a0\u00a0Prompts"}</p>
        <p className="mt-1 text-sm text-ink-soft">
          {shown} of {total} — pick one and the chat opens with it typed in
        </p>

        <span className="relative mt-4 block">
          <Search className="pointer-events-none absolute start-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-soft" />
          <input
            data-testid="prompt-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search prompts…"
            className="min-h-[44px] w-full rounded-full border border-white/10 bg-page ps-10 pe-4 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
          />
        </span>

        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="prompt-category-filters">
          <button
            data-testid="prompt-category-all"
            onClick={() => setCat("")}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              cat === "" ? "border-brand bg-brand/15 text-brand" : "border-white/10 text-ink-soft hover:text-ink"
            }`}
          >
            Everything
          </button>
          <button
            data-testid="prompt-sort-used"
            onClick={() => setByUse((v) => !v)}
            title="Show the ones I use most first"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              byUse ? "border-brand bg-brand/15 text-brand" : "border-white/10 text-ink-soft hover:text-ink"
            }`}
          >
            <BarChart3 className="h-3.5 w-3.5" /> Most used
          </button>
          {custom.length > 0 && (
            <button
              data-testid="prompt-category-mine"
              onClick={() => setCat(cat === "mine" ? "" : "mine")}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                cat === "mine" ? "border-brand bg-brand/15 text-brand" : "border-white/10 text-ink-soft hover:text-ink"
              }`}
            >
              My prompts
            </button>
          )}
          {PROMPT_CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`prompt-category-${c.id}`}
              onClick={() => setCat(cat === c.id ? "" : c.id)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                cat === c.id ? "border-brand bg-brand/15 text-brand" : "border-white/10 text-ink-soft hover:text-ink"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 pb-10">
        {shown === 0 && (
          <p className="mt-6 text-sm text-ink-soft" data-testid="prompt-drawer-empty">
            Nothing matches that — try another word.
          </p>
        )}

        {groups.map((group) => (
          <div key={group.id} className="mt-7">
            <p
              data-testid={`prompt-group-${group.id}`}
              onDragOver={(e) => group.mine && onReorder && e.preventDefault()}
              onDrop={(e) => {
                if (!group.mine || !onReorder) return;
                e.preventDefault();
                dropOnFolder(group.items[0]?.folder || "");
              }}
              className={`text-xs font-semibold uppercase tracking-widest transition-colors ${
                group.mine && dragId ? "text-brand" : "text-ink-soft"
              }`}
            >
              {group.label}
            </p>
            <div className={twoColumns ? "mt-3 grid gap-2 md:grid-cols-2" : "mt-3 space-y-2"}>
              {group.items.map((p) => (
                <button
                  key={p.id}
                  data-testid={`prompt-row-${p.id}`}
                  onClick={() => onPick(p.id)}
                  draggable={Boolean(group.mine && onReorder && !byUse)}
                  onDragStart={() => setDragId(p.id)}
                  onDragOver={(e) => group.mine && onReorder && dragId && e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    drop(p);
                  }}
                  onDragEnd={() => setDragId(null)}
                  className={`group flex w-full items-center gap-4 rounded-2xl border bg-page p-3 text-start transition-colors hover:border-brand/60 ${
                    dragId === p.id ? "border-brand/60 opacity-60" : "border-white/10"
                  }`}
                >
                  {group.mine && onReorder && !byUse && (
                    <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-ink-soft" data-testid={`prompt-row-drag-${p.id}`} />
                  )}
                  {p.tile ? (
                    <img src={p.tile} alt="" loading="lazy" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
                  ) : (
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-white/10 bg-panel">
                      <PromptIcon name={p.icon} className={`h-5 w-5 ${group.tint}`} />
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-sm font-bold tracking-tight">{p.label}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-ink-soft">
                      {p.blurb}
                      {uses(p) > 0 && (
                        <span data-testid={`prompt-row-uses-${p.id}`} className="text-brand">
                          {p.blurb ? " · " : ""}used {uses(p)} {uses(p) === 1 ? "time" : "times"}
                        </span>
                      )}
                    </span>
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={`prompt-row-share-${p.id}`}
                    aria-label={`Share ${p.label}`}
                    title="Copy a link to this prompt"
                    onClick={(e) => {
                      e.stopPropagation();
                      share(p);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        e.preventDefault();
                        share(p);
                      }
                    }}
                    className="shrink-0 rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                  </span>
                  {!p.mine && onDuplicate && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`prompt-row-duplicate-${p.id}`}
                      aria-label={`Copy ${p.label} to my prompts`}
                      title="Copy this to my prompts and tweak the wording"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(p);
                      }}
                      className="shrink-0 rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                    >
                      <CopyPlus className="h-3.5 w-3.5" />
                    </span>
                  )}
                  {p.mine && onEdit && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`prompt-row-edit-${p.id}`}
                      aria-label={`Edit ${p.label}`}
                      title="Edit this prompt"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(p);
                      }}
                      className="shrink-0 rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </span>
                  )}
                  {p.mine && onDelete && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`prompt-row-delete-${p.id}`}
                      aria-label={`Delete ${p.label}`}
                      title="Delete this prompt"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(p);
                      }}
                      className="shrink-0 rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-destructive/60 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </span>
                  )}
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={`prompt-row-pin-${p.id}`}
                    aria-label={pins.includes(p.id) ? `Unpin ${p.label}` : `Pin ${p.label}`}
                    title={pins.includes(p.id) ? "Unpin from the board" : "Pin to the top of the board"}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTogglePin?.(p.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        e.preventDefault();
                        onTogglePin?.(p.id);
                      }
                    }}
                    className={`shrink-0 rounded-full border p-1.5 transition-colors ${
                      pins.includes(p.id)
                        ? "border-brand bg-brand/15 text-brand"
                        : "border-white/10 text-ink-soft hover:border-brand/50 hover:text-brand"
                    }`}
                  >
                    {pins.includes(p.id) ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-ink-soft transition-transform group-hover:translate-x-0.5 group-hover:text-brand" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
